import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wallet, CheckCircle, XCircle, ArrowDownLeft, AlertTriangle, Clock, History, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { adminApi } from '../../api/adminApi';
import './Employees.css';
import './AdminCashTransfers.css';

const AdminCashTransfers = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('PENDING');

    const [loading, setLoading] = useState(true);
    const [transactions, setTransactions] = useState([]);

    // Pagination State correctly mapped to backend
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, total: 0 });

    const [confirmModal, setConfirmModal] = useState({ show: false, action: null, transferId: null, title: '', message: '' });
    const [actionProcessing, setActionProcessing] = useState(false);
    const [infoModal, setInfoModal] = useState({ show: false, title: '', message: '', isError: false });

    // Ensure we pass the page number to the backend
    const fetchTransfers = async (tab, page = 1) => {
        setLoading(true);
        try {
            if (tab === 'PENDING') {
                const res = await adminApi.cashTransfers.getIncoming();
                if (res.data.success) {
                    setTransactions(res.data.data.transfers);
                    // Pending list usually doesn't have backend pagination
                    setPagination({ currentPage: 1, totalPages: 1, total: res.data.data.transfers.length });
                }
            } else {
                // Fetch paginated history (e.g., 20 items per page for a compact list)
                const res = await adminApi.cashTransfers.getHistory({ page, limit: 20 });
                if (res.data.success) {
                    setTransactions(res.data.data.transfers);
                    setPagination(res.data.data.pagination);
                }
            }
        } catch (err) {
            console.error("Failed to load transfers", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransfers(activeTab, 1);
    }, [activeTab]);

    // Handle Pagination Clicks
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchTransfers(activeTab, newPage);
        }
    };

    const handleActionClick = (transferId, action) => {
        const isConfirm = action === 'confirm';
        setConfirmModal({
            show: true,
            action,
            transferId,
            title: isConfirm ? 'Confirm Receipt' : 'Reject Transfer',
            message: isConfirm
                ? 'Are you sure you have physically received this cash?'
                : 'Are you sure you want to reject this deposit?'
        });
    };

    const executeAction = async () => {
        const { action, transferId } = confirmModal;
        setActionProcessing(true);

        try {
            if (action === 'confirm') {
                await adminApi.cashTransfers.confirm(transferId);
            } else {
                await adminApi.cashTransfers.reject(transferId);
            }
            setConfirmModal({ show: false, action: null, transferId: null, title: '', message: '' });
            // Refresh to page 1 of current tab
            fetchTransfers(activeTab, 1);
            setInfoModal({ show: true, title: "Success", message: `Transfer successfully ${action === 'confirm' ? 'confirmed' : 'rejected'}.`, isError: false });
        } catch (err) {
            setConfirmModal({ show: false, action: null, transferId: null, title: '', message: '' });
            setInfoModal({ show: true, title: "Action Failed", message: err.response?.data?.message || `Failed to ${action} transfer`, isError: true });
        } finally {
            setActionProcessing(false);
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

    return (
        <div className="admin-employees-container">
            <header className="dashboard-header groups-header">
                <div className="header-left">
                    <button className="elder-back-btn" onClick={() => navigate('/admin/employees')}>
                        <ArrowLeft size={24} /> <span>Back</span>
                    </button>
                </div>
                <div className="header-center">
                    <h1 className="page-title">Cash Transfers Hub</h1>
                </div>
                <div className="header-right"></div>
            </header>

            <main className="employees-main-content">

                <div className="wallet-controls">
                    <div className="wallet-tabs">
                        <button className={`w-tab ${activeTab === 'PENDING' ? 'active' : ''}`} onClick={() => setActiveTab('PENDING')}>
                            <Clock size={18} /> Pending Deposits
                        </button>
                        <button className={`w-tab ${activeTab === 'HISTORY' ? 'active' : ''}`} onClick={() => setActiveTab('HISTORY')}>
                            <History size={18} /> Global History
                        </button>
                    </div>
                </div>

                <div className="admin-tx-list-container">
                    {loading ? (
                        <div className="elder-empty-card no-border"><div className="spinner"></div></div>
                    ) : transactions.length === 0 ? (
                        <div className="elder-empty-card no-border">
                            <Wallet size={48} className="empty-wallet-icon" />
                            <p>No {activeTab.toLowerCase()} transfers found.</p>
                        </div>
                    ) : (
                        <div className={activeTab === 'HISTORY' ? "compact-history-wrapper" : "elder-list-container"}>
                            {transactions.map(tx => {
                                const isIncomingToAdmin = tx.to?.role === 'ADMIN';

                                // --- PENDING TAB: Uses standard larger cards for actionable items ---
                                if (activeTab === 'PENDING') {
                                    return (
                                        <div key={tx._id} className="elder-list-card">
                                            <div className="list-card-left">
                                                <div className="icon-wrapper bg-emerald-light text-emerald">
                                                    <ArrowDownLeft size={24} />
                                                </div>
                                                <div className="list-card-info">
                                                    <h3 className="elder-card-title">From: {tx.from.name}</h3>
                                                    <p className="tx-date">
                                                        {new Date(tx.createdAt).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                    {tx.reason && <p className="tx-reason">"{tx.reason}"</p>}
                                                </div>
                                            </div>

                                            <div className="admin-tx-right">
                                                <h2 className="admin-tx-amount text-emerald">
                                                    {formatCurrency(tx.amount)}
                                                </h2>
                                                <div className="admin-tx-actions">
                                                    <button className="admin-tx-btn btn-reject-tx" onClick={() => handleActionClick(tx._id, 'reject')}>
                                                        <XCircle size={16} /> Reject
                                                    </button>
                                                    <button className="admin-tx-btn btn-confirm-tx" onClick={() => handleActionClick(tx._id, 'confirm')}>
                                                        <CheckCircle size={16} /> Confirm
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // --- HISTORY TAB: Uses ultra-compact rows for scanning ---
                                return (
                                    <div key={tx._id} className="compact-history-row">
                                        <div className="chr-left">
                                            <div className={`chr-icon ${isIncomingToAdmin ? 'bg-emerald-light text-emerald' : 'bg-blue-light text-blue'}`}>
                                                {isIncomingToAdmin ? <ArrowDownLeft size={16} /> : <ArrowRight size={16} />}
                                            </div>
                                            <div className="chr-info">
                                                <div className="chr-title">
                                                    {tx.from.name} <ArrowRight size={12} className="chr-arrow" /> {isIncomingToAdmin ? 'Admin' : tx.to.name}
                                                </div>
                                                <div className="chr-meta">
                                                    {new Date(tx.createdAt).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    {tx.reason && <span className="chr-reason">• "{tx.reason}"</span>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="chr-right">
                                            <div className={`chr-amount ${isIncomingToAdmin ? 'text-emerald' : 'text-blue'}`}>
                                                {formatCurrency(tx.amount)}
                                            </div>
                                            <div className={`w-status ${tx.status === 'CONFIRMED' ? 's-green' : tx.status === 'REJECTED' ? 's-red' : tx.status === 'CANCELLED' ? 's-gray' : 's-yellow'}`}>
                                                {tx.status}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* --- FRONTEND PAGINATION UI --- */}
                {!loading && activeTab === 'HISTORY' && pagination.totalPages > 1 && (
                    <div className="elder-pagination">
                        <button
                            className="page-btn"
                            disabled={pagination.currentPage === 1}
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                        >
                            <ChevronLeft size={24} /> Prev
                        </button>
                        <span className="page-info">
                            Page {pagination.currentPage} of {pagination.totalPages}
                        </span>
                        <button
                            className="page-btn"
                            disabled={pagination.currentPage === pagination.totalPages}
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                        >
                            Next <ChevronRight size={24} />
                        </button>
                    </div>
                )}
            </main>

            {/* Custom Confirmation Modal */}
            {confirmModal.show && (
                <div className="elder-modal-overlay">
                    <div className="elder-modal-card confirm-dialog-card">
                        <div className={`confirm-icon-wrapper ${confirmModal.action === 'confirm' ? 'bg-emerald-light text-emerald' : 'bg-red-light text-red'}`}>
                            {confirmModal.action === 'confirm' ? <CheckCircle size={36} /> : <AlertTriangle size={36} />}
                        </div>
                        <h3>{confirmModal.title}</h3>
                        <p>{confirmModal.message}</p>
                        <div className="modal-actions">
                            <button className="elder-btn-secondary" onClick={() => setConfirmModal({ show: false, action: null, transferId: null, title: '', message: '' })} disabled={actionProcessing}>Cancel</button>
                            <button
                                className={`elder-btn-primary full-width-center ${confirmModal.action === 'cancel' ? 'btn-danger-fill' : 'btn-success-fill'}`}
                                onClick={executeAction}
                                disabled={actionProcessing}
                            >
                                {actionProcessing ? 'Processing...' : `Yes, ${confirmModal.action === 'confirm' ? 'Confirm' : 'Reject'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Info Modal */}
            {infoModal.show && (
                <div className="elder-modal-overlay">
                    <div className="elder-modal-card confirm-dialog-card">
                        <div className={`confirm-icon-wrapper ${infoModal.isError ? 'bg-red-light text-red' : 'bg-emerald-light text-emerald'}`}>
                            {infoModal.isError ? <XCircle size={36} /> : <CheckCircle size={36} />}
                        </div>
                        <h3>{infoModal.title}</h3>
                        <p>{infoModal.message}</p>
                        <div className="modal-actions">
                            <button className="elder-btn-primary full-width-center" onClick={() => setInfoModal({ show: false, title: '', message: '', isError: false })}>Got it</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCashTransfers;